import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, ShoppingCart, Loader2, X, Calendar, Users, Bed } from 'lucide-react';
import { toast } from 'react-hot-toast';
import hotelService from '../services/hotelService';
import cartService from '../services/cartService';
import { buildHotelCartItemFromPriceCheck } from '../utils/hotelCartUtils';
import {
  buildAgentCommissionSnapshot,
  getDefaultCommissionPctForRateSource
} from '../utils/agentCommissionCart';
import { resolveRateSourceFromPlan } from '../utils/rateSource';
import travelAgentService from '../services/travelAgentService';
import { useCart } from '../contexts/CartContext';

/**
 * Parse rooms from getAvailableRoomsForHotelDetails response (same shape as RoomExplorerSelection).
 */
function parseRooms(roomsData, searchCriteria) {
  if (!roomsData) return [];
  let rooms = [];
  if (roomsData?.rooms) {
    rooms = roomsData.rooms;
  } else if (roomsData?.GetHotelRateInfoRS?.HotelRateInfos?.HotelRateInfo?.[0]?.Rooms?.Room) {
    rooms = roomsData.GetHotelRateInfoRS.HotelRateInfos.HotelRateInfo[0].Rooms.Room;
  } else if (roomsData?.data?.GetHotelRateInfoRS?.HotelRateInfos?.HotelRateInfo?.[0]?.Rooms?.Room) {
    rooms = roomsData.data.GetHotelRateInfoRS.HotelRateInfos.HotelRateInfo[0].Rooms.Room;
  }
  if (!rooms?.length) return [];

  const nights = hotelService.calculateNights(searchCriteria.checkInDate, searchCriteria.checkOutDate);
  const rows = [];
  rooms.forEach((room) => {
    const roomTypeName = room.RoomType || room.RoomDescription?.Name || 'Standard Room';
    const bedInfo =
      room.BedTypeOptions?.BedTypes?.[0]?.BedType?.map((b) => `${b.Count || 1} ${b.Description || 'Bed'}`).join(', ') ||
      'Standard Bed';
    const ratePlans = room.RatePlans?.RatePlan
      ? Array.isArray(room.RatePlans.RatePlan)
        ? room.RatePlans.RatePlan
        : [room.RatePlans.RatePlan]
      : [];
    ratePlans.forEach((plan) => {
      const convertedRateInfo = plan.ConvertedRateInfo || {};
      const totalAmount = parseFloat(convertedRateInfo.AmountAfterTax || 0);
      const averageNightlyRate = parseFloat(convertedRateInfo.AverageNightlyRate || 0);
      const pricePerNight = averageNightlyRate || (nights > 0 ? totalAmount / nights : totalAmount);
      const currency = convertedRateInfo.CurrencyCode || searchCriteria.currency || 'USD';
      const cancelPenalties = convertedRateInfo.CancelPenalties?.CancelPenalty;
      let cancellationDetails = 'Non-refundable';
      if (cancelPenalties?.length) {
        const p = cancelPenalties[0];
        if (p.Refundable === true) {
          const days = p.OffsetUnitMultiplier || 0;
          cancellationDetails =
            days > 0
              ? `Free cancellation up to ${days} day${days > 1 ? 's' : ''} before arrival`
              : 'Free cancellation until check-in';
        }
      }
      rows.push({
        room: {
          ...room,
          roomTypeName,
          bedInfo,
          maxOccupancy: { adults: room.Adults || 2, children: room.Children || 0 },
          RoomTypeCode: room.RoomTypeCode
        },
        ratePlan: {
          ...plan,
          RateKey: plan.RateKey,
          ratePlanName: plan.RatePlanName || 'Standard Rate',
          RatePlanType: plan.RatePlanType,
          rateSource: plan.RateSource || plan.rateSource || '100',
          pricing: { total: totalAmount, perNight: pricePerNight, currency },
          cancellationDetails,
          additionalDetails: convertedRateInfo.AdditionalDetails?.AdditionalDetail || []
        }
      });
    });
  });
  return rows;
}

const ConciergeHotelRates = ({ hotel, searchParams, onAddedToCart, cardKey, onExpandChange, compactLayout }) => {
  const { refreshCartCount } = useCart();
  const [expanded, setExpanded] = useState(false);
  const [roomsData, setRoomsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [agentMarkupSettings, setAgentMarkupSettings] = useState({
    loaded: false,
    markupEnabled: false,
    markupPercentage: 10
  });

  const hotelCode = hotel?.hotelcode || hotel?.hotelCode || hotel?.sabreHotelCode;
  const checkInDate = searchParams?.checkInDate || searchParams?.checkIn;
  const checkOutDate = searchParams?.checkOutDate || searchParams?.checkOut;
  const adults = searchParams?.adults ?? 2;
  const children = searchParams?.children ?? 0;
  const currency = searchParams?.currency || 'USD';

  const searchCriteria = useMemo(
    () => ({
      checkInDate,
      checkOutDate,
      adults,
      children,
      currency
    }),
    [checkInDate, checkOutDate, adults, children, currency]
  );

  const rateRows = useMemo(() => {
    if (!roomsData) return [];
    return parseRooms(roomsData, searchCriteria);
  }, [roomsData, searchCriteria]);

  const isValidSearch =
    hotelCode && checkInDate && checkOutDate && new Date(checkInDate) < new Date(checkOutDate);

  useEffect(() => {
    if (!expanded || !isValidSearch || roomsData !== null) return;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      checkInDate,
      checkOutDate,
      adults: String(adults),
      children: String(children),
      currency
    });
    hotelService
      .getAvailableRoomsForHotelDetails(hotelCode, params.toString())
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        setRoomsData(data);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load rates for this hotel');
          setRoomsData({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, isValidSearch, hotelCode, checkInDate, checkOutDate, adults, children, currency]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await travelAgentService.getProfile();
        const d = res?.data;
        if (cancelled || !d) return;
        const raw = d.markupPercentage;
        const n = raw === null || raw === undefined ? 10 : Number(raw);
        const pct = Number.isNaN(n) ? 10 : Math.min(100, Math.max(10, n));
        setAgentMarkupSettings({
          loaded: true,
          markupEnabled: d.markupEnabled !== false,
          markupPercentage: pct
        });
      } catch {
        if (!cancelled) setAgentMarkupSettings((p) => ({ ...p, loaded: true }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAddToCart = async (row) => {
    if (!row.ratePlan.RateKey) {
      toast.error('This rate cannot be added to cart');
      return;
    }
    setAddToCartLoading(row);
    try {
      const priceCheckResult = await hotelService.priceCheckForBooking({
        rateKey: row.ratePlan.RateKey,
        checkInDate,
        checkOutDate
      });
      const data = priceCheckResult?.data ?? priceCheckResult;
      if (!data?.HotelPriceCheckRS?.PriceCheckInfo) {
        throw new Error('Price check failed');
      }
      const cartData = buildHotelCartItemFromPriceCheck(
        data,
        { room: row.room, ratePlan: row.ratePlan },
        searchCriteria,
        { imageUrl: hotel?.imageUrl || '' }
      );
      const totalRate = cartData.price;
      const { rateSource, rateSourceLabel } = resolveRateSourceFromPlan(row.ratePlan);
      const commissionFloor = getDefaultCommissionPctForRateSource(rateSource);
      cartData.rateSource = rateSource;
      cartData.rateSourceLabel = rateSourceLabel;
      cartData.agentCommission = {
        ...buildAgentCommissionSnapshot({
          baseBookingTotal: totalRate,
          markupPercentage: commissionFloor,
          markupEnabled: agentMarkupSettings.markupEnabled,
          defaultCommissionPct: commissionFloor,
          selectionId: null
        }),
        markupBaselinePct: commissionFloor
      };
      await cartService.addToCart(cartData);
      refreshCartCount();
      toast.success('Added to cart');
      onAddedToCart?.();
      setPreviewRow(null);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to add to cart');
    } finally {
      setAddToCartLoading(null);
    }
  };

  const nights = hotelService.calculateNights(checkInDate, checkOutDate);

  if (!hotelCode || !isValidSearch) {
    return (
      <p className="text-xs text-gray-500 mt-2">
        Add check-in and check-out dates to view rates and add to cart.
      </p>
    );
  }

  const panelTransition = { type: 'spring', stiffness: 400, damping: 30 };
  const bouncy = { type: 'spring', stiffness: 500, damping: 25 };

  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <motion.button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((prev) => {
            const next = !prev;
            if (onExpandChange && cardKey != null) onExpandChange(next, cardKey);
            return next;
          });
        }}
        className={`inline-flex items-center justify-center gap-1.5 w-full sm:w-auto text-xs font-medium rounded-md transition-colors ${
          expanded
            ? 'text-gray-700 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 border border-gray-200'
            : 'text-gray-900 bg-white border border-gray-300 hover:border-gray-400 hover:bg-gray-50 shadow-sm px-3 py-2'
        }`}
        whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        whileTap={{ scale: 0.98 }}
        transition={bouncy}
      >
        <motion.span
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={panelTransition}
          className="inline-flex items-center justify-center"
        >
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </motion.span>
        {expanded ? 'Hide rates' : 'View rates & add to cart'}
      </motion.button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className={`${compactLayout ? 'mt-2' : 'mt-3'} overflow-hidden`}
            initial={{ opacity: 0, scaleY: 0.88, y: -12 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.88, y: -8 }}
            transition={{ ...panelTransition, opacity: { duration: 0.25 } }}
            style={{ transformOrigin: 'top' }}
          >
            <div className="rounded-xl border border-gray-200 bg-gray-50/80 overflow-hidden">
              <motion.div
                className="px-3 py-2 border-b border-gray-200 bg-white/60"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08, ...panelTransition }}
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Available rates
                </span>
              </motion.div>
              <div className="p-3 space-y-3 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {loading && (
                  <motion.div
                    className="flex items-center justify-center gap-2 text-sm text-gray-500 py-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                    <span>Loading rates…</span>
                  </motion.div>
                )}
                {!loading && rateRows.length === 0 && (
                  <motion.p
                    className="text-sm text-gray-500 py-6 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    No rates available for these dates.
                  </motion.p>
                )}
                {!loading &&
                  rateRows.map((row, idx) => (
                    <motion.div
                      key={`${row.room.roomTypeName}-${row.ratePlan.ratePlanName}-${idx}`}
                      initial={{ opacity: 0, y: 14, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 28, delay: idx * 0.05 }}
                      whileHover={{ y: -2, boxShadow: '0 8px 20px rgba(0,0,0,0.06)' }}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg bg-white border border-gray-100 p-3 shadow-sm"
                    >
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-semibold text-gray-900">{row.room.roomTypeName}</p>
                    {row.ratePlan.ratePlanName && row.ratePlan.ratePlanName !== 'Standard Rate' && (
                      <p className="text-xs text-gray-500">{row.ratePlan.ratePlanName}</p>
                    )}
                    <p className="text-sm font-medium text-gray-900">
                      {row.ratePlan.pricing.currency} {row.ratePlan.pricing.perNight?.toFixed(2)}
                      <span className="text-gray-500 font-normal ml-0.5">/night</span>
                    </p>
                    <p className="text-xs text-gray-500">{row.ratePlan.cancellationDetails}</p>
                  </div>
                  <motion.button
                    type="button"
                    disabled={addToCartLoading !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewRow(row);
                    }}
                    className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-black disabled:opacity-50 transition-colors"
                    whileHover={addToCartLoading === null ? { scale: 1.03 } : undefined}
                    whileTap={addToCartLoading === null ? { scale: 0.97 } : undefined}
                    transition={bouncy}
                  >
                    {addToCartLoading === row ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ShoppingCart className="h-4 w-4" />
                    )}
                    Add to cart
                  </motion.button>
                </motion.div>
              ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rate details popup before add to cart */}
      <AnimatePresence>
        {previewRow && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => !addToCartLoading && setPreviewRow(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="px-5 pt-5 pb-4 border-b border-gray-100 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{hotel?.hotelName}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">Review rate details before adding to cart</p>
                </div>
                <button
                  type="button"
                  onClick={() => !addToCartLoading && setPreviewRow(null)}
                  disabled={!!addToCartLoading}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{previewRow.room.roomTypeName}</h4>
                  {previewRow.ratePlan.ratePlanName && previewRow.ratePlan.ratePlanName !== 'Standard Rate' && (
                    <p className="text-xs text-gray-500">{previewRow.ratePlan.ratePlanName}</p>
                  )}
                </div>
                {previewRow.room.bedInfo && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Bed className="h-4 w-4 text-gray-400 shrink-0" />
                    <span>{previewRow.room.bedInfo}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                  <span>
                    {checkInDate && new Date(checkInDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    {' → '}
                    {checkOutDate && new Date(checkOutDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Users className="h-4 w-4 text-gray-400 shrink-0" />
                  <span>{adults} adult{adults !== 1 ? 's' : ''}{children > 0 ? `, ${children} child${children !== 1 ? 'ren' : ''}` : ''}</span>
                </div>
                <div className="pt-2 border-t border-gray-100 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Price per night</span>
                    <span className="font-semibold text-gray-900">
                      {previewRow.ratePlan.pricing.currency} {previewRow.ratePlan.pricing.perNight?.toFixed(2)}
                    </span>
                  </div>
                  {nights > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total ({nights} night{nights !== 1 ? 's' : ''})</span>
                      <span className="font-semibold text-gray-900">
                        {previewRow.ratePlan.pricing.currency} {(previewRow.ratePlan.pricing.total ?? previewRow.ratePlan.pricing.perNight * nights).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-500 mb-0.5">Cancellation</p>
                  <p className="text-sm text-gray-700">{previewRow.ratePlan.cancellationDetails}</p>
                </div>
              </div>
              <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                <button
                  type="button"
                  onClick={() => setPreviewRow(null)}
                  disabled={!!addToCartLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <motion.button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToCart(previewRow);
                  }}
                  disabled={!!addToCartLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
                  whileHover={!addToCartLoading ? { scale: 1.02 } : undefined}
                  whileTap={!addToCartLoading ? { scale: 0.98 } : undefined}
                  transition={bouncy}
                >
                  {addToCartLoading === previewRow ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Adding…
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4" />
                      Confirm & add to cart
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConciergeHotelRates;
